<template>
  <div class="share-legend-editor">
    <div class="d-flex align-center justify-space-between mb-3">
      <div class="text-subtitle-2 font-weight-bold">Danh sách chú giải</div>
      <div>
        <v-btn color="primary" small outlined @click="openDialog('list')" :disabled="!mapShareId || loading">
          <v-icon small class="mr-1">mdi-plus</v-icon> Thêm chú giải
        </v-btn>
        <input type="file" ref="fileReplaceInput" style="display: none" accept="image/png, image/jpeg, image/svg+xml"
          @change="onFileReplaced" />
      </div>
    </div>

    <v-alert v-if="!mapShareId" type="info" dense text>
      Vui lòng lưu bản đồ lần đầu tiên để có thể thêm chú giải.
    </v-alert>

    <v-card outlined v-if="mapShareId">
      <v-list dense>
        <draggable v-model="legends" @change="onDragChange" handle=".drag-handle">
          <div v-for="(item, index) in legends" :key="item.id">
            <v-list-item class="legend-list-item">
              <v-list-item-action class="mr-2">
                <v-icon class="drag-handle" style="cursor: grab;" title="Kéo thả để sắp xếp">mdi-drag</v-icon>
              </v-list-item-action>

              <v-list-item-content>
                <div v-if="item.type === 'list'" class="d-flex align-center">
                  <div v-if="item.color" class="color-box" :style="{ backgroundColor: item.color }"></div>
                  <span class="ml-3" :class="{ 'font-weight-bold': item.children && item.children.length }">{{
                    item.title
                    }}</span>
                </div>
                <div v-else-if="item.type === 'image'" class="d-flex align-center">
                  <v-img :src="getImageUrl(item.image_path)" max-width="150" max-height="80" contain
                    position="left"></v-img>
                  <span class="ml-3 text-caption grey--text">{{ item.title || '(Ảnh)' }}</span>
                </div>
              </v-list-item-content>

              <v-list-item-action class="flex-row align-center">
                <v-btn v-if="item.type === 'image'" icon small color="success" class="mr-1" title="Đổi ảnh"
                  @click="triggerReplace(item)">
                  <v-icon small>mdi-image-sync</v-icon>
                </v-btn>
                <v-btn icon small color="primary" @click="editItem(item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="error" class="ml-1" @click="deleteItem(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>

            <!-- CHILDREN -->
            <div class="pl-10 pr-2 bg-grey-lighten-4" v-if="item.children && item.children.length > 0">
              <draggable v-model="item.children" @change="onDragChangeChild" handle=".drag-handle">
                <v-list-item v-for="(child) in item.children" :key="child.id" class="legend-list-item child-item">
                  <v-list-item-action class="mr-2">
                    <v-icon class="drag-handle" style="cursor: grab;" title="Kéo thả để sắp xếp">mdi-drag</v-icon>
                  </v-list-item-action>

                  <v-list-item-content>
                    <div v-if="child.type === 'list'" class="d-flex align-center">
                      <div v-if="child.color" class="color-box" :style="{ backgroundColor: child.color }"></div>
                      <span class="ml-3">{{ child.title }}</span>
                    </div>
                    <div v-else-if="child.type === 'image'" class="d-flex align-center">
                      <v-img :src="getImageUrl(child.image_path)" max-width="150" max-height="80" contain
                        position="left"></v-img>
                      <span class="ml-3 text-caption grey--text">{{ child.title || '(Ảnh)' }}</span>
                    </div>
                  </v-list-item-content>

                  <v-list-item-action class="flex-row align-center">
                    <v-btn v-if="child.type === 'image'" icon small color="success" class="mr-1" title="Đổi ảnh"
                      @click="triggerReplace(child)">
                      <v-icon small>mdi-image-sync</v-icon>
                    </v-btn>
                    <v-btn icon small color="primary" @click="editItem(child)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn icon small color="error" class="ml-1" @click="deleteItem(child)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </draggable>
            </div>

            <v-divider v-if="index < legends.length - 1"></v-divider>
          </div>
        </draggable>
        <v-list-item v-if="legends.length === 0">
          <v-list-item-content>
            <div class="text-center grey--text py-4">Chưa có chú giải nào</div>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>

    <v-dialog v-model="dialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ editId ? 'Sửa chú giải' : 'Thêm chú giải' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" lazy-validation>
            <!-- Parent selection (Only available if not editing a parent that has children) -->
            <v-select v-if="!editId || !hasChildren(editId)" v-model="formData.parent_id" :items="parentLegends"
              item-text="title" item-value="id" label="Thuộc nhóm (Tuỳ chọn)" outlined dense clearable
              class="mb-2"></v-select>

            <!-- Type selection (Only when creating) -->
            <v-radio-group v-if="!editId" v-model="formData.type" row class="mt-0 pt-0 mb-2" hide-details>
              <v-radio label="Chữ/Màu" value="list"></v-radio>
              <v-radio label="Ảnh" value="image"></v-radio>
            </v-radio-group>

            <template v-if="formData.type === 'list'">
              <v-text-field v-model="formData.title" label="Nhãn chú giải" outlined dense
                :rules="[v => !!v || 'Vui lòng nhập nhãn']"></v-text-field>
              <v-checkbox v-if="!formData.parent_id" v-model="isGroup"
                label="Tạo dưới dạng Tiêu đề nhóm (Không kèm màu sắc)" dense hide-details class="mb-3"></v-checkbox>
              <div v-if="!isGroup || formData.parent_id">
                <div class="mt-2 mb-1">Màu sắc</div>
                <field-group onlyColor v-model="formData.color" type-input="field-color-picker"></field-group>
              </div>
            </template>

            <template v-if="formData.type === 'image'">
              <v-text-field v-model="formData.title" label="Nhãn chú giải (Tuỳ chọn)" outlined dense></v-text-field>
              <v-file-input v-if="!editId" v-model="formData.imageFile" label="Chọn ảnh"
                accept="image/png, image/jpeg, image/svg+xml" outlined dense
                :rules="[v => !!v || 'Vui lòng chọn ảnh']"></v-file-input>
              <div v-else class="text-caption grey--text mb-2">* Để đổi ảnh, dùng nút (<v-icon small
                  color="success">mdi-image-sync</v-icon>) ở màn hình danh sách.</div>
            </template>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Hủy</v-btn>
          <v-btn color="primary" @click="saveLegend" :loading="loading">Lưu</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { API_MAPSHARE_LEGEND } from '@api'
import draggable from 'vuedraggable'

export default {
  name: 'ShareLegendEditor',
  components: { draggable },
  props: {
    mapShareId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      legends: [],
      loading: false,
      dialog: false,
      editId: null,
      isGroup: false,
      formData: {
        type: 'list',
        title: '',
        color: null,
        parent_id: null,
        imageFile: null
      },
      replacingItem: null
    }
  },
  watch: {
    mapShareId(newVal) {
      if (newVal) {
        this.fetchLegends()
      } else {
        this.legends = []
      }
    }
  },
  computed: {
    parentLegends() {
      return this.legends
        .filter(item => item.type === 'list')
        .map(item => ({
          id: item.id,
          title: item.title ? item.title : '[Không tên]'
        }))
    }
  },
  mounted() {
    if (this.mapShareId) {
      this.fetchLegends()
    }
  },
  methods: {
    getImageUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      let baseUrl = process.env.VUE_APP_API_URL || ''
      if (baseUrl.endsWith('/api/') || baseUrl.endsWith('/api')) {
        baseUrl = baseUrl.replace(/\/api\/?$/, '')
      }
      return baseUrl ? `${baseUrl}${path}` : path
    },
    async fetchLegends() {
      if (!this.mapShareId) return
      this.loading = true
      try {
        const res = await API_MAPSHARE_LEGEND.list(this.mapShareId)
        if (Array.isArray(res)) {
          this.legends = res
        } else if (res && res.data) {
          this.legends = res.data
        } else {
          this.legends = []
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    hasChildren(id) {
      const item = this.legends.find(x => x.id === id)
      return item && item.children && item.children.length > 0
    },
    openDialog(type, item = null) {
      this.formData.type = type
      if (item) {
        this.editId = item.id
        this.formData.title = item.title || ''
        this.formData.color = item.color || null
        this.formData.parent_id = item.parent_id || null
        this.formData.imageFile = null
        this.isGroup = !item.color && !item.parent_id
      } else {
        this.editId = null
        this.formData.title = ''
        this.formData.color = type === 'list' ? '#000000' : null
        this.formData.parent_id = null
        this.formData.imageFile = null
        this.isGroup = false
      }
      this.dialog = true
      if (this.$refs.form) this.$refs.form.resetValidation()
    },
    async saveLegend() {
      if (!this.$refs.form.validate()) return
      this.loading = true
      try {
        let payload
        if (this.formData.type === 'image') {
          payload = new FormData()
          if (!this.editId) payload.append('map_share_id', this.mapShareId)
          payload.append('type', 'image')
          if (this.formData.title) payload.append('title', this.formData.title)
          if (this.formData.parent_id) payload.append('parent_id', this.formData.parent_id)
          if (this.formData.imageFile && !this.editId) payload.append('image', this.formData.imageFile)
        } else {
          payload = {
            map_share_id: this.mapShareId,
            type: 'list',
            title: this.formData.title,
            color: (!this.formData.parent_id && this.isGroup) ? '' : (this.formData.color || ''),
            parent_id: this.formData.parent_id || null
          }
        }

        if (this.editId) {
          await API_MAPSHARE_LEGEND.update(this.editId, payload)
        } else {
          await API_MAPSHARE_LEGEND.create(payload)
        }
        this.dialog = false
        await this.fetchLegends()
      } catch (error) {
        this.$store.dispatch('notify/error', { message: 'Có lỗi xảy ra khi lưu chú giải' })
      } finally {
        this.loading = false
      }
    },
    editItem(item) {
      this.openDialog(item.type, item)
    },
    triggerReplace(item) {
      this.replacingItem = item
      this.$refs.fileReplaceInput.click()
    },
    async onFileReplaced(e) {
      const file = e.target.files[0]
      if (!file || !this.replacingItem) return
      this.$refs.fileReplaceInput.value = ''

      const formData = new FormData()
      formData.append('type', 'image')
      formData.append('image', file)

      this.loading = true
      try {
        await API_MAPSHARE_LEGEND.update(this.replacingItem.id, formData)
        await this.fetchLegends()
      } catch (error) {
        this.$store.dispatch('notify/error', { message: 'Lỗi khi thay thế ảnh' })
      } finally {
        this.loading = false
        this.replacingItem = null
      }
    },
    async deleteItem(item) {
      let msg = 'Bạn có chắc chắn muốn xóa chú giải này?'
      if (item.children && item.children.length > 0) {
        msg = 'Chú ý: Chú giải này đang chứa các mục con. Xóa nó sẽ xóa TẤT CẢ các mục con bên trong! Bạn có chắc chắn?'
      }
      if (!confirm(msg)) return
      this.loading = true
      try {
        await API_MAPSHARE_LEGEND.delete(item.id)
        await this.fetchLegends()
      } catch (error) {
        this.$store.dispatch('notify/error', { message: 'Lỗi khi xóa chú giải' })
      } finally {
        this.loading = false
      }
    },
    async onDragChange(event) {
      if (!event.moved) return
      const { element, newIndex } = event.moved
      try {
        await API_MAPSHARE_LEGEND.update(element.id, { order: newIndex })
      } catch (error) {
        this.$store.dispatch('notify/error', { message: 'Lỗi khi lưu vị trí' })
        this.fetchLegends()
      }
    },
    async onDragChangeChild(event) {
      if (!event.moved) return
      const { element, newIndex } = event.moved
      try {
        await API_MAPSHARE_LEGEND.update(element.id, { order: newIndex })
      } catch (error) {
        this.$store.dispatch('notify/error', { message: 'Lỗi khi lưu vị trí' })
        this.fetchLegends()
      }
    }
  }
}
</script>

<style scoped>
.color-box {
  width: 24px;
  height: 24px;
  border: 1px solid #ddd;
  border-radius: 4px;
  flex-shrink: 0;
}

.legend-list-item {
  padding: 0px 8px;
}

.child-item {
  border-left: 2px solid #ddd;
  margin-left: 8px;
}

.bg-grey-lighten-4 {
  background-color: #f5f5f5;
}
</style>
